// Extracted from original/src/App.tsx:57529-57800 (edit-user dialog; the add-user dialog :57264-57528 shares
// the markup, header icon UserPlus :57282). Kept: backdrop, card, header, form layout, input classes, the
// permission checkbox grid (:57644-57677), the allowed-scope block pattern (:57679-57770, warehouses → boxes),
// the confirmation block (:57775-57789, super code → own PIN, D-018) and the submit button. Added (D-130, D-131):
// display name, role select from the roles table, permission groups, working-hours rows, IQD / USD limits,
// two-factor flag. Cut: `editingUser` state, `ALL_PERMISSIONS` auto-fill by role (:57625-57636), handleUpdateUser.
import { Edit2, UserPlus, X } from 'lucide-react';
import { motion } from 'motion/react';
import type { PermissionDef } from '@faylasuf/contracts';
import { useI18n } from '../i18n-context.js';

export interface WorkingHoursRange {
  readonly weekday: number;
  readonly from: string;
  readonly to: string;
}

export interface UserFormValues {
  readonly username: string;
  readonly displayName: string;
  readonly employeeCode: string;
  readonly roleId: string;
  /** Effective permission set shown as checkboxes; the container derives grants / revocations (D-130). */
  readonly permissions: readonly string[];
  readonly twoFactorRequired: boolean;
  readonly workingHours: readonly WorkingHoursRange[] | null;
  readonly limitIqd: string;
  readonly limitUsd: string;
  /** `null` = all boxes. */
  readonly allowedBoxIds: readonly string[] | null;
}

export interface UserDialogProps {
  readonly mode: 'create' | 'edit';
  readonly values: UserFormValues;
  readonly onChange: (patch: Partial<UserFormValues>) => void;
  readonly roles: readonly { readonly id: string; readonly name: string; readonly builtin: string | null }[];
  readonly permissionCatalogue: readonly PermissionDef[];
  readonly boxes: readonly { readonly id: string; readonly name: string }[];
  readonly pin: string;
  readonly onPinChange: (value: string) => void;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
  readonly error?: string | null;
  readonly busy?: boolean;
  /** Shown after a successful create (D-037: the one-time setup link is handed to the user). */
  readonly setupLink?: string | null;
}

const LABEL = "block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest text-right";
const INPUT = "w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] font-black transition-all";

const GROUP_ORDER: readonly PermissionDef['group'][] = ['sections', 'dashboard_cards', 'movements', 'customers', 'money', 'custody', 'settings', 'users', 'audit'];

export function UserDialog(props: UserDialogProps) {
  const { t, locale } = useI18n();
  const v = props.values;
  const isEdit = props.mode === 'edit';
  const dir = locale === 'ar' ? 'rtl' : 'ltr';
  const groups = GROUP_ORDER.map((g) => ({ group: g, items: props.permissionCatalogue.filter((p) => p.group === g) })).filter((g) => g.items.length > 0);
  const restrictedBoxes = v.allowedBoxIds !== null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!props.busy) props.onSubmit();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="user-dialog">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={props.onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border flex flex-col max-h-[90vh]"
      >
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-blue-50/50 dark:bg-blue-900/10">
          <h3 className="text-xl font-black flex items-center gap-2 dark:text-[#f0f6fc] uppercase tracking-tight dark:digital-glow">
            {isEdit ? <Edit2 size={24} className="text-blue-500" /> : <UserPlus size={24} className="text-blue-500" />}
            {isEdit ? t('users.edit_title') : t('users.add_new')}
          </h3>
          <button
            type="button"
            onClick={props.onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors cursor-pointer"
          >
            <X size={24} />
          </button>
        </div>
        {props.setupLink ? (
          <div className="p-6 space-y-4 text-right" dir={dir}>
            <label className={LABEL}>{t('users.setup_link')}</label>
            <div data-testid="user-setup-link" dir="ltr" className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 font-mono text-xs font-bold text-gray-700 dark:text-[#f0f6fc] break-all select-all">
              {props.setupLink}
            </div>
            <button
              type="button"
              onClick={props.onClose}
              className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-all cursor-pointer"
            >
              {t('common.close')}
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="p-6 space-y-4 overflow-y-auto custom-scrollbar flex-1 text-right"
            dir={dir}
          >
            <div>
              <label className={LABEL}>{t('users.username')}</label>
              <input
                required
                type="text"
                data-testid="user-username"
                value={v.username}
                onChange={(e) => props.onChange({ username: e.target.value })}
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL}>{t('users.display_name')}</label>
              <input
                required
                type="text"
                data-testid="user-display-name"
                value={v.displayName}
                onChange={(e) => props.onChange({ displayName: e.target.value })}
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL}>{t('users.employee_code')}</label>
              <input
                maxLength={2}
                type="text"
                value={v.employeeCode}
                onChange={(e) => props.onChange({ employeeCode: e.target.value })}
                className={`${INPUT} font-mono uppercase text-center`}
                placeholder="AA"
              />
            </div>
            <div>
              <label className={LABEL}>{t('users.role')}</label>
              <select
                value={v.roleId}
                data-testid="user-role"
                onChange={(e) => props.onChange({ roleId: e.target.value })}
                className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all font-black text-right"
              >
                {props.roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL}>{t('users.permissions')}</label>
              <div className="space-y-3" dir={dir}>
                {groups.map((g) => (
                  <div key={g.group}>
                    <p className="text-[10px] font-black text-gray-500 dark:text-[#8b949e] uppercase tracking-wider mb-1.5">{t(`perm.group.${g.group}`)}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {g.items.map((perm) => {
                        const label = locale === 'ar' ? perm.ar : perm.en;
                        const checked = v.permissions.includes(perm.token);
                        return (
                          <label
                            key={perm.token}
                            className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-[#05070a] rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-[#161b22] transition-colors border border-transparent dark:border-[#30363d]"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              data-testid={`perm-${perm.token}`}
                              onChange={(e) => {
                                const updated = e.target.checked ? [...v.permissions, perm.token] : v.permissions.filter((p) => p !== perm.token);
                                props.onChange({ permissions: updated });
                              }}
                              className="w-4 h-4 rounded text-blue-600 dark:bg-[#0d1117] dark:border-[#30363d]"
                            />
                            <span className="text-[10px] font-black dark:text-[#f0f6fc] text-right">
                              {label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* D-131 restrictions */}
            <div className="space-y-3 bg-gray-50/70 dark:bg-[#05070a] p-3 rounded-2xl border border-gray-100 dark:border-[#30363d]" dir={dir}>
              <span className="text-xs font-bold text-gray-700 dark:text-gray-300 block">{t('users.restrictions')}</span>
              <label className="flex items-center gap-2 p-2 bg-white dark:bg-[#0d1117] rounded-lg cursor-pointer border border-gray-200 dark:border-[#30363d]">
                <input
                  type="checkbox"
                  checked={v.twoFactorRequired}
                  onChange={(e) => props.onChange({ twoFactorRequired: e.target.checked })}
                  className="w-4 h-4 rounded text-blue-600 dark:bg-[#0d1117] dark:border-[#30363d]"
                />
                <span className="text-[10px] font-black dark:text-[#f0f6fc]">{t('users.two_factor_required')}</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={LABEL}>{t('users.limit_iqd')}</label>
                  <input type="text" inputMode="decimal" dir="ltr" value={v.limitIqd} placeholder={t('users.no_restriction')} data-testid="user-limit-iqd"
                    onChange={(e) => props.onChange({ limitIqd: e.target.value })} className={`${INPUT} font-mono text-center`} />
                </div>
                <div>
                  <label className={LABEL}>{t('users.limit_usd')}</label>
                  <input type="text" inputMode="decimal" dir="ltr" value={v.limitUsd} placeholder={t('users.no_restriction')} data-testid="user-limit-usd"
                    onChange={(e) => props.onChange({ limitUsd: e.target.value })} className={`${INPUT} font-mono text-center`} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between pb-2 border-b border-gray-200/60 dark:border-[#30363d]">
                  <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{t('users.working_hours')}</span>
                  <button
                    type="button"
                    onClick={() => props.onChange({ workingHours: [...(v.workingHours ?? []), { weekday: 0, from: "08:00", to: "17:00" }] })}
                    className="text-[11px] font-black text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                  >
                    {t('users.add_working_hours')}
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 font-normal mt-1">{t('users.working_hours_hint')}</p>
                <div className="space-y-2 mt-2">
                  {(v.workingHours ?? []).map((r, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                      <select
                        value={r.weekday}
                        onChange={(e) => props.onChange({ workingHours: (v.workingHours ?? []).map((x, j) => (j === i ? { ...x, weekday: Number(e.target.value) } : x)) })}
                        className="bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2 text-xs font-bold dark:text-[#f0f6fc]"
                      >
                        {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                          <option key={d} value={d}>{t(`weekday.${d}`)}</option>
                        ))}
                      </select>
                      <input type="time" value={r.from} dir="ltr" onChange={(e) => props.onChange({ workingHours: (v.workingHours ?? []).map((x, j) => (j === i ? { ...x, from: e.target.value } : x)) })}
                        className="bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-xl px-2 py-2 text-xs font-mono font-bold dark:text-[#f0f6fc]" />
                      <input type="time" value={r.to} dir="ltr" onChange={(e) => props.onChange({ workingHours: (v.workingHours ?? []).map((x, j) => (j === i ? { ...x, to: e.target.value } : x)) })}
                        className="bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-xl px-2 py-2 text-xs font-mono font-bold dark:text-[#f0f6fc]" />
                      <button type="button" onClick={() => props.onChange({ workingHours: (v.workingHours ?? []).filter((_, j) => j !== i) })}
                        className="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 p-2 rounded-lg transition-colors">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              {/* Allowed boxes (pattern of App.tsx:57692-57770) */}
              <div>
                <div className="flex items-center justify-between pb-2 border-b border-gray-200/60 dark:border-[#30363d]">
                  <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                    {t('users.allowed_boxes')}
                  </span>
                  <button
                    type="button"
                    onClick={() => props.onChange({ allowedBoxIds: restrictedBoxes ? null : [] })}
                    className="text-[11px] font-black text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                  >
                    {restrictedBoxes ? t('users.allow_all_boxes') : t('users.restrict_boxes')}
                  </button>
                </div>
                {!restrictedBoxes ? (
                  <div className="p-2.5 mt-2 bg-blue-50/60 dark:bg-blue-950/20 rounded-xl text-center border border-blue-100 dark:border-blue-900/30">
                    <p className="text-xs font-black text-blue-700 dark:text-blue-300">
                      🌐 {t('users.all_boxes')}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {props.boxes.map((box) => {
                      const isChecked = (v.allowedBoxIds ?? []).includes(box.id);
                      return (
                        <label
                          key={box.id}
                          className={`flex items-center gap-2 p-2.5 rounded-xl cursor-pointer transition-all border ${
                            isChecked
                              ? "bg-blue-50/80 dark:bg-blue-950/40 border-blue-300 dark:border-blue-800 text-blue-900 dark:text-blue-100 font-bold"
                              : "bg-white dark:bg-[#0d1117] border-gray-200 dark:border-[#30363d] text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#161b22]"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const current = v.allowedBoxIds ?? [];
                              props.onChange({ allowedBoxIds: e.target.checked ? [...current, box.id] : current.filter((id) => id !== box.id) });
                            }}
                            className="w-4 h-4 rounded text-blue-600 dark:bg-[#0d1117] dark:border-[#30363d]"
                          />
                          <div className="flex flex-col text-right">
                            <span className="text-xs font-black">🏢 {box.name}</span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 bg-rose-50/50 dark:bg-rose-950/10 rounded-2xl border border-rose-100/50 dark:border-rose-900/20 space-y-3">
              <span className="text-xs font-bold text-rose-600 dark:text-rose-400 block text-right">
                {t('users.pin_confirm')}
              </span>
              <input
                required
                type="password"
                inputMode="numeric"
                maxLength={6}
                autoComplete="off"
                data-testid="user-pin"
                value={props.pin}
                onChange={(e) => props.onPinChange(e.target.value)}
                placeholder={t('users.pin_placeholder')}
                className="w-full bg-slate-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/20 dark:text-[#f0f6fc] font-mono text-center text-lg font-bold tracking-widest transition-all"
              />
            </div>
            {props.error && (
              <p data-testid="user-dialog-error" className="text-xs font-bold text-rose-500 text-center">{props.error}</p>
            )}
            <button
              type="submit"
              disabled={props.busy}
              data-testid="user-submit"
              className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-all cursor-pointer disabled:opacity-50"
            >
              {isEdit ? t('users.save') : t('users.create')}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
